import { IconChevronDown } from "@tabler/icons-react";
import { useId, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { selectTriggerClassName } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface FilterChecklistItem {
  value: string;
  label: string;
}

export interface FilterChecklistSelectProps {
  ariaLabel: string;
  placeholder: string;
  items: readonly FilterChecklistItem[];
  value: readonly string[];
  onValueChange: (value: string[]) => void;
  /** Label for the count of further selections, e.g. "+3". */
  moreLabel: (count: number) => string;
  searchPlaceholder: string;
  /** e.g. "全选 (2/5)" — counts what the search currently shows. */
  selectAllLabel: (selected: number, total: number) => string;
  /** e.g. "仅展示已选项(2)". */
  selectedOnlyLabel: (count: number) => string;
  noResults: string;
  className?: string;
}

/**
 * A FilterMultiSelect for longer option lists: a quick search, a select-all
 * over what the search shows, and a "selected only" view. An empty selection
 * still means no filter.
 */
export function FilterChecklistSelect({
  ariaLabel,
  placeholder,
  items,
  value,
  onValueChange,
  moreLabel,
  searchPlaceholder,
  selectAllLabel,
  selectedOnlyLabel,
  noResults,
  className,
}: FilterChecklistSelectProps): React.ReactElement {
  const listId = useId();
  const [search, setSearch] = useState("");
  // The rows "selected only" shows, fixed when it is turned on so unticking
  // one does not pull it out from under the pointer.
  const [selectedOnly, setSelectedOnly] = useState<ReadonlySet<string> | null>(null);
  const selected = new Set(value);
  const selectedCount = items.filter((item) => selected.has(item.value)).length;
  const term = search.trim().toLowerCase();
  const visible = items.filter(
    (item) =>
      (!term || item.label.toLowerCase().includes(term)) &&
      (!selectedOnly || selectedOnly.has(item.value)),
  );
  const visibleSelected = visible.filter((item) => selected.has(item.value)).length;
  const allVisibleSelected = visible.length > 0 && visibleSelected === visible.length;
  const labelOf = (key: string) => items.find((item) => item.value === key)?.label ?? key;
  const isApplied = value.length > 0;

  // Keeps the caller's option order, whatever order things were ticked in.
  const commit = (next: Set<string>) =>
    onValueChange(items.map((item) => item.value).filter((key) => next.has(key)));
  const toggle = (key: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(key);
    else next.delete(key);
    commit(next);
  };
  const toggleVisible = () => {
    const next = new Set(selected);
    for (const item of visible) {
      if (allVisibleSelected) next.delete(item.value);
      else next.add(item.value);
    }
    commit(next);
  };

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) return;
        setSearch("");
        setSelectedOnly(null);
      }}
    >
      <PopoverTrigger
        aria-label={
          value.length === 0 ? ariaLabel : `${ariaLabel}: ${value.map(labelOf).join(", ")}`
        }
        data-size="sm"
        data-filter-applied={isApplied || undefined}
        className={cn(
          selectTriggerClassName,
          "min-w-[128px] gap-[7px] px-2.5",
          isApplied && "border-transparent bg-control-highlight",
          className,
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          {value.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <>
              <span className="truncate">{labelOf(value[0] ?? "")}</span>
              {value.length > 1 && (
                <span className="shrink-0 rounded-sm bg-muted px-1 text-[10px] leading-4 text-muted-foreground">
                  {moreLabel(value.length - 1)}
                </span>
              )}
            </>
          )}
        </span>
        <IconChevronDown aria-hidden className="size-4 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-64" aria-label={ariaLabel}>
        <div className="flex flex-col gap-2 text-xs">
          <Input
            type="search"
            size="sm"
            value={search}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            aria-controls={listId}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="flex items-center justify-between gap-2">
            {/* biome-ignore lint/a11y/noLabelWithoutControl: the Checkbox renders the control. */}
            <label className="flex items-center gap-2 font-medium text-ink">
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={visibleSelected > 0 && !allVisibleSelected}
                disabled={visible.length === 0}
                onCheckedChange={toggleVisible}
              />
              {selectAllLabel(visibleSelected, visible.length)}
            </label>
            <button
              type="button"
              aria-pressed={selectedOnly !== null}
              className={cn(
                "rounded-sm text-muted-foreground outline-none hover:text-ink focus-visible:ring-2 focus-visible:ring-ring",
                selectedOnly && "font-medium text-primary hover:text-primary",
              )}
              onClick={() => setSelectedOnly((current) => (current ? null : new Set(value)))}
            >
              {selectedOnlyLabel(selectedCount)}
            </button>
          </div>
          <div id={listId} className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="py-4 text-center text-muted-foreground">{noResults}</p>
            ) : (
              visible.map((item) => (
                // biome-ignore lint/a11y/noLabelWithoutControl: the Checkbox renders the control.
                <label
                  key={item.value}
                  className="flex min-h-7 cursor-default items-center gap-2 rounded-sm px-1 hover:bg-accent"
                >
                  <Checkbox
                    checked={selected.has(item.value)}
                    onCheckedChange={(checked) => toggle(item.value, checked)}
                  />
                  <span className="truncate" title={item.label}>
                    {item.label}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
}
